import { useLayoutEffect, useState } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';

// trendio-selfcare-ui-kit
import {
  NavItem,
  NavGroup,
  HORIZONTAL_MAX_ITEM,
  MenuOrientation,
  useGetMenuMaster,
  useConfig,
  NavItemType
} from '@trendiolive/trendio-selfcare-ui-kit';

// project import
import { getMenuItems } from 'menu-items';
import useAuth from 'hooks/useAuth';
import { OPTIMIZED_VIDEO_ITEM_ID, video } from 'menu-items/video-ai';
import messagesDashboard from 'menu-items/message-center';
import { DISPLAY_AFFILIATE_FINDER, DISPLAY_AI_MESSAGE_CENTER, DISPLAY_CRM, SHOW_VIDEO_AI } from 'constants/waffleConstants';
import { affiliateCommunity, simplifiedAffiliateCommunity } from 'menu-items/affiliate-community';
import { crm } from 'menu-items/crm';
import { useAppSelector } from 'app/hooks';
import useTTSShopPermissions from 'features/permissions/hooks/useTTSShopPermissions';

// types

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

function videoGroupWithoutOptimizedVideo(group: NavItemType): NavItemType {
  return {
    ...group,
    children: group.children?.map((collapse) => ({
      ...collapse,
      children: collapse.children?.filter((item) => item.id !== OPTIMIZED_VIDEO_ITEM_ID)
    }))
  };
}

export default function Navigation() {
  const { flags } = useAuth();
  const sps = useAppSelector((store) => store.merchant.sps);
  const { hasTiktokShopFeature } = useTTSShopPermissions();
  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const [selectedID, setSelectedID] = useState<string | undefined>('');
  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });

  useLayoutEffect(() => {
    function setMenu() {
      const items: { items: NavItemType[] } = getMenuItems({ shopPerformanceScore: sps, hasTiktokShopFeature, flags });

      if (!flags?.includes(SHOW_VIDEO_AI)) {
        items.items = items.items.map((item) => (item === video ? videoGroupWithoutOptimizedVideo(item) : item));
      }

      // crm menu display
      if (!flags?.includes(DISPLAY_CRM)) {
        items.items = items.items.filter((item) => item !== crm);
      }

      // handle lists menu items display
      if (!flags?.includes(DISPLAY_AFFILIATE_FINDER)) {
        items.items = items.items.filter((item) => item !== affiliateCommunity);
      } else {
        items.items = items.items.filter((item) => item !== simplifiedAffiliateCommunity);
      }

      // ai message center menu display
      if (!flags?.includes(DISPLAY_AI_MESSAGE_CENTER)) {
        items.items = items.items.filter((item) => item !== messagesDashboard);
      }

      setMenuItems(items);
    }
    setMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flags, sps, hasTiktokShopFeature]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
  // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    if (item.type !== 'group') {
      return (
        <Typography key={item.id} variant="h6" color="error" align="center">
          Fix - Navigation Group
        </Typography>
      );
    }

    if (item.url && item.id !== lastItemId) {
      return (
        <List key={item.id} {...(isHorizontal && { sx: { mt: 0.5 } })}>
          <NavItem item={item} level={1} isParents />
        </List>
      );
    }

    return (
      <NavGroup
        key={item.id}
        setSelectedID={setSelectedID}
        setSelectedItems={setSelectedItems}
        setSelectedLevel={setSelectedLevel}
        selectedLevel={selectedLevel}
        selectedID={selectedID}
        selectedItems={selectedItems}
        lastItem={lastItem!}
        remItems={remItems}
        lastItemId={lastItemId}
        item={item}
      />
    );
  });

  const drawerOpenPadding = isHorizontal ? 0 : 2;

  return (
    <Box
      sx={{
        pt: drawerOpen ? drawerOpenPadding : 0,
        ...(!isHorizontal && { '& > ul:first-of-type': { mt: 0 } }),
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
}
